import { serverTimestamp } from "firebase/firestore";
import {
  addEmailToDb,
  addHistoryToDb,
  addNotificationToDb,
  getUserProfileFromDb,
  sendNotificationEmail,
} from "../../../../../app/firestore/firestoreService";

function getTransactionAddress(transaction) {
  return transaction?.address?.street || transaction?.title || "";
}

export async function getSignatureRecipientProfile(transaction, roleKey) {
  const userId = roleKey === "coagent" ? transaction?.coAgentId : transaction?.userId;
  const fallbackProfile =
    roleKey === "coagent"
      ? { ...transaction?.coAgent, role: "CoAgent" }
      : transaction?.agentProfile;

  if (!userId) return fallbackProfile || null;

  try {
    return (await getUserProfileFromDb(userId)) || fallbackProfile || null;
  } catch (error) {
    console.log("Could not load signature recipient profile", error);
    return fallbackProfile || null;
  }
}

export async function notifySignerToSign({
  transaction,
  doc,
  currentUserProfile,
  roleKey,
  recipientProfile,
}) {
  const recipientId = roleKey === "coagent" ? transaction?.coAgentId : transaction?.userId;
  const roleLabel = roleKey === "coagent" ? "Co-Agent" : "Agent";
  const profile =
    recipientProfile || (await getSignatureRecipientProfile(transaction, roleKey));
  const recipientEmail = profile?.email || "";
  const transactionAddress = getTransactionAddress(transaction);
  const transactionTitle = transaction?.title || "";
  const agentRepresents = transaction?.agentRepresents || "";
  const subject = "Your signature is required on a document";
  const addressOrTitle = transactionAddress?.length > 8 ? transactionAddress : transactionTitle;
  const message = `Your signature is required on "${doc?.name || "a document"}" for ${agentRepresents} ${addressOrTitle}.`;

  await addNotificationToDb({
    transactionId: transaction?.id || "",
    docId: doc?.id || "",
    docName: doc?.name || "",
    type: "signatureRequired",
    status: "unresolved",
    transactionAddress,
    agentId: recipientId || "",
    recipientIds: recipientId ? [recipientId] : [],
    signatureRoleKey: roleKey,
    signerFirstName: profile?.firstName || "",
    signerLastName: profile?.lastName || "",
    signerEmail: recipientEmail,
    recipientFirstName: profile?.firstName || "",
    recipientLastName: profile?.lastName || "",
    recipientEmail,
    message: "Your signature is required on this document.",
    partyFirstName: currentUserProfile?.firstName || "",
    partyLastName: currentUserProfile?.lastName || "",
    partyRole: currentUserProfile?.role || "",
    tcId: transaction?.tcId || "",
    agentAsTcId: transaction?.agentAsTcId || "",
    managerId: transaction?.managerId || "",
  });

  if (recipientEmail) {
    const mailMessage = {
      subject,
      text: message,
      html: `
        <div style="font-family: Arial, sans-serif; max-width: 800px; margin: 0 auto;">
          <p>Hi ${profile?.firstName || "there"},</p>
          <p>Your signature is required on a document:</p>
          <p><strong>${doc?.name || "Document"}</strong></p>
          <p>${addressOrTitle}</p>
          <p>Representing: ${agentRepresents}</p>
          <hr />
          <a href="https://app.transactioner.com">Login to Sign</a>
          <hr />
          <p style="color: #666; font-size: 12px;">
            Sent from Transactioner by ${currentUserProfile?.firstName || ""} ${
        currentUserProfile?.lastName || ""
      }
          </p>
        </div>
      `,
    };

    await sendNotificationEmail({
      to: recipientEmail,
      from: `${currentUserProfile?.firstName || ""} ${
        currentUserProfile?.lastName || ""
      } <help@transactioner.com>`,
      replyTo: currentUserProfile?.email || "help@transactioner.com",
      message: mailMessage,
    });

    await addEmailToDb(
      {
        transactionId: transaction?.id || "",
        transactionTitle: transaction?.title || "",
        from: currentUserProfile?.email || "",
        fromName: `${currentUserProfile?.firstName || ""} ${
          currentUserProfile?.lastName || ""
        }`.trim(),
        to: recipientEmail,
        toEmails: [recipientEmail],
        toEmailsAndRoles: [
          {
            role: roleKey,
            email: recipientEmail,
            firstName: profile?.firstName || "",
            lastName: profile?.lastName || "",
          },
        ],
        subject,
        message,
        type: "signatureRequired-notification",
        sentAt: serverTimestamp(),
      },
      { message: mailMessage }
    );
  }

  await addHistoryToDb(
    transaction?.id,
    currentUserProfile,
    "notified needs to sign",
    doc?.name,
    { ...profile, role: profile?.role || roleLabel }
  );

  return profile;
}